"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Badge } from "@/components/ui/badge"

// Framer Motion typing workaround for className support
const MotionDiv: any = motion.div

interface BadgeCarouselProps {
  className?: string
  badgeClassName?: string
}

export function BadgeCarousel({ 
  className = "",
  badgeClassName = "inline-flex items-center bg-gray-50 text-gray-700 pl-1 pr-2 py-0.5 border border-gray-200 rounded-full text-xs font-medium hover:bg-white"
}: BadgeCarouselProps) {
  const [isOpenSeaExpanded, setIsOpenSeaExpanded] = useState(true)
  
  // Toggle badge state every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsOpenSeaExpanded(prev => !prev)
    }, 8000)
    
    return () => clearInterval(interval)
  }, [])

  // Simple static text
  const StaticText = ({ text }: { text: string }) => {
    return (
      <span className="text-gray-700 whitespace-nowrap">
        {text}
      </span>
    )
  }

  const openseaIcon = (
    <svg 
      aria-label="verified-icon" 
      className="w-5 h-5" 
      fill="#2081e2" 
      viewBox="0 0 30 30"
    >
      <path
        d="M13.474 2.80108C14.2729 1.85822 15.7271 1.85822 16.526 2.80108L17.4886 3.9373C17.9785 4.51548 18.753 4.76715 19.4892 4.58733L20.9358 4.23394C22.1363 3.94069 23.3128 4.79547 23.4049 6.0278L23.5158 7.51286C23.5723 8.26854 24.051 8.92742 24.7522 9.21463L26.1303 9.77906C27.2739 10.2474 27.7233 11.6305 27.0734 12.6816L26.2903 13.9482C25.8918 14.5928 25.8918 15.4072 26.2903 16.0518L27.0734 17.3184C27.7233 18.3695 27.2739 19.7526 26.1303 20.2209L24.7522 20.7854C24.051 21.0726 23.5723 21.7315 23.5158 22.4871L23.4049 23.9722C23.3128 25.2045 22.1363 26.0593 20.9358 25.7661L19.4892 25.4127C18.753 25.2328 17.9785 25.4845 17.4886 26.0627L16.526 27.1989C15.7271 28.1418 14.2729 28.1418 13.474 27.1989L12.5114 26.0627C12.0215 25.4845 11.247 25.2328 10.5108 25.4127L9.06418 25.7661C7.86371 26.0593 6.6872 25.2045 6.59513 23.9722L6.48419 22.4871C6.42773 21.7315 5.94903 20.7854 5.24777 20.7854L3.86969 20.2209C2.72612 19.7526 2.27673 18.3695 2.9266 17.3184L3.70973 16.0518C4.10824 15.4072 4.10824 14.5928 3.70973 13.9482L2.9266 12.6816C2.27673 11.6305 2.72612 10.2474 3.86969 9.77906L5.24777 9.21463C5.94903 8.92742 6.42773 8.26854 6.48419 7.51286L6.59513 6.0278C6.6872 4.79547 7.86371 3.94069 9.06418 4.23394L10.5108 4.58733C11.247 4.76715 12.0215 4.51548 12.5114 3.9373L13.474 2.80108Z"
        className="sc-9c65691d-1 Sxbrk"
      />
      <path
        d="M13.5 17.625L10.875 15L10 15.875L13.5 19.375L21 11.875L20.125 11L13.5 17.625Z"
        fill="white"
        stroke="white"
      />
    </svg>
  )

  const delegatedIcon = (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_620_1039)">
        <path d="M15.6092 11.7861C18.1118 9.72481 20.5152 7.74131 22.0124 5.50717C21.719 5.10096 21.3134 4.77686 20.8474 4.57808L12.7226 1.12532C12.5414 1.04321 12.3472 1 12.1444 1C11.9459 1 11.7475 1.04321 11.5705 1.12532L3.44141 4.57808C2.49215 4.97997 1.78452 5.9177 1.75 7.011C1.80178 10.6064 3.02287 16.6303 7.70015 20.606C9.0852 17.1619 12.399 14.4351 15.6092 11.7861Z" fill="#04724D"/>
        <path d="M13.2836 22.7457C20.718 19.1806 22.4785 11.3373 22.5 7.01164C22.5044 6.45419 22.3188 5.93563 22.0125 5.50781C20.5195 7.74195 18.1119 9.72545 15.6093 11.7867C12.399 14.4357 9.08525 17.1625 7.7002 20.6066C8.65377 21.4147 9.74542 22.1407 11.0054 22.7457C11.7259 23.0871 12.563 23.0871 13.2836 22.7457Z" fill="#FFC300"/>
      </g>
    </svg>
  )

  return (
    <div className={`flex items-center gap-1 h-7 ${className}`}>
      {/* OpenSea бейдж (слева) */}
      <div className="relative h-7 flex items-center">
        <MotionDiv
          className="relative overflow-hidden rounded-full"
          animate={{
            width: isOpenSeaExpanded ? 228 : 28,
          }}
          transition={{ 
            duration: 0.7, 
            ease: [0.25, 0.1, 0.25, 1]
          }}
        >
          <Badge 
            variant="outline" 
            className={`h-7 bg-gray-50 text-gray-700 border border-gray-200 rounded-full text-xs font-funnel font-medium uppercase flex items-center relative ${
              isOpenSeaExpanded 
                ? 'pl-1 pr-2.5 hover:bg-white' 
                : 'justify-center'
            }`}
          >
            {/* Фиксированная иконка */}
            <MotionDiv
              className="w-5 h-5 flex items-center absolute left-[3px]"
              initial={false}
              animate={{
                rotateY: isOpenSeaExpanded ? [0, 720] : 0,
              }}
              transition={{
                delay: isOpenSeaExpanded ? 0.3 : 0,
                duration: isOpenSeaExpanded ? 1.2 : 0,
                ease: "easeOut",
                type: "spring",
                stiffness: 100,
                damping: 25,
                mass: 1,
              }}
              style={{
                transformOrigin: "center",
                transformStyle: "preserve-3d",
              }}
            >
              {openseaIcon}
            </MotionDiv>
            
            {/* Простой текст */}
            {isOpenSeaExpanded && (
              <div className="ml-6">
                <a
                  href="https://opensea.io/collection/nftotem"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="OpenSea Badged Collection"
                >
                  <StaticText text="OpenSea Badged Collection" />
                </a>
              </div>
            )}
          </Badge>
        </MotionDiv>
      </div>

      {/* Delegated Wallet Support бейдж (справа) */}
      <div className="relative h-7 flex items-center">
        <MotionDiv
          className="relative overflow-hidden rounded-full"
          animate={{
            width: !isOpenSeaExpanded ? 220 : 28,
          }}
          transition={{ 
            duration: 0.7, 
            ease: [0.25, 0.1, 0.25, 1]
          }}
        >
          <Badge 
            variant="outline" 
            className={`h-7 bg-gray-50 text-gray-700 border border-gray-200 rounded-full text-xs font-funnel font-medium uppercase flex items-center relative ${
              !isOpenSeaExpanded 
                ? 'pl-2 pr-2.5' 
                : 'justify-center'
            }`}
          >
            {/* Фиксированная иконка */}
            <MotionDiv
              className="w-4 h-4 flex items-center absolute left-[5px]"
              initial={false}
              animate={{
                rotateY: !isOpenSeaExpanded ? [0, 720] : 0,
              }}
              transition={{
                delay: !isOpenSeaExpanded ? 0.3 : 0,
                duration: !isOpenSeaExpanded ? 1.2 : 0,
                ease: "easeOut",
                type: "spring",
                stiffness: 100,
                damping: 25,
                mass: 1,
              }}
              style={{
                transformOrigin: "center",
                transformStyle: "preserve-3d",
              }}
            >
              {delegatedIcon}
            </MotionDiv>
            
            {/* Простой текст */}
            {!isOpenSeaExpanded && (
              <div className="ml-5">
                <StaticText text="Delegated Wallet Support" />
              </div>
            )}
          </Badge>
        </MotionDiv>
      </div>
    </div>
  )
}
